import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { Upload, Check, X, Download, FileText } from 'lucide-react';
import { useAuth } from '@/store/auth';

interface DocLista {
  id: number;
  lista_id: number;
  codigo: string;
  bloque: string;
  tipo: string;
  origen: 'jne_oficial' | 'op_interno';
  nombre: string;
  es_documento_declara: boolean;
  resolucion_jne: string | null;
  archivo_nombre: string | null;
  archivo_url: string | null;
  archivo_tipo: string | null;
  archivo_tamano_bytes: number | null;
  fecha_carga: string | null;
  estado: 'pendiente' | 'recibido' | 'aprobado_plt' | 'rechazado_plt';
  aplica: boolean;
  notas_revision: string | null;
}

const ESTADO_BADGE: Record<string, { cls: string; label: string }> = {
  pendiente: { cls: 'bg-gray-100 text-gray-600', label: '⏳ Pendiente' },
  recibido: { cls: 'bg-yellow-100 text-yellow-800', label: '🟡 Recibido' },
  aprobado_plt: { cls: 'bg-green-100 text-green-700', label: '✅ Aprobado' },
  rechazado_plt: { cls: 'bg-red-100 text-red-700', label: '❌ Rechazado' },
};

function formatSize(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function DocumentosListaTab({ listaId }: { listaId: number }) {
  const { rol } = useAuth();
  const qc = useQueryClient();
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [uploadingCodigo, setUploadingCodigo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canEdit = ['PLT', 'PLA', 'ADM', 'ASI'].includes(rol ?? '');
  const canApprove = ['PLT', 'PLA', 'ADM'].includes(rol ?? '');

  const { data: docs, isLoading } = useQuery<DocLista[]>({
    queryKey: ['lista-docs', listaId],
    queryFn: async () => (await api.get<DocLista[]>(`/listas/${listaId}/documentos`)).data,
  });

  const uploadMut = useMutation({
    mutationFn: async ({ codigo, file }: { codigo: string; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      setUploadingCodigo(codigo);
      try {
        const { data } = await api.post<DocLista>(`/listas/${listaId}/documentos/${codigo}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
      } finally {
        setUploadingCodigo(null);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lista-docs', listaId] }),
    onError: (e: any) => setError(e?.response?.data?.detail || 'Error al subir'),
  });

  const aprobarMut = useMutation({
    mutationFn: async (docId: number) => (await api.post<DocLista>(`/documentos-lista/${docId}/aprobar`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lista-docs', listaId] }),
    onError: (e: any) => setError(e?.response?.data?.detail || 'Error al aprobar'),
  });

  const rechazarMut = useMutation({
    mutationFn: async ({ docId, motivo }: { docId: number; motivo: string }) =>
      (await api.post<DocLista>(`/documentos-lista/${docId}/rechazar`, { motivo })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lista-docs', listaId] }),
  });

  if (isLoading || !docs) return <div className="p-4 text-sm text-gray-500">Cargando documentos de la lista…</div>;

  const oficiales = docs.filter((d) => d.origen === 'jne_oficial');
  const internos = docs.filter((d) => d.origen === 'op_interno');

  const totales = {
    aprobados: docs.filter((d) => d.estado === 'aprobado_plt').length,
    recibidos: docs.filter((d) => d.estado === 'recibido').length,
    pendientes: docs.filter((d) => d.estado === 'pendiente').length,
    rechazados: docs.filter((d) => d.estado === 'rechazado_plt').length,
    total: docs.length,
  };

  function GrupoTabla({ titulo, items, intent }: { titulo: string; items: DocLista[]; intent: 'oficial' | 'interno' }) {
    if (items.length === 0) return null;
    return (
      <div className={`bg-white border rounded ${intent === 'oficial' ? 'border-blue-300' : 'border-gray-300'}`}>
        <div className={`px-3 py-2 border-b text-xs font-semibold uppercase ${intent === 'oficial' ? 'bg-blue-50 text-blue-900' : 'bg-gray-50 text-gray-700'}`}>
          {intent === 'oficial' && '⚖️ '}{titulo} ({items.length})
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs">
            <tr>
              <th className="px-3 py-2 text-left w-14">Cód</th>
              <th className="px-3 py-2 text-left">Documento</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-left">Archivo</th>
              <th className="px-3 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => {
              const eb = ESTADO_BADGE[d.estado];
              const isUp = uploadingCodigo === d.codigo;
              return (
                <tr key={d.id} className="border-t">
                  <td className="px-3 py-2 font-mono text-xs">{d.codigo}</td>
                  <td className="px-3 py-2 text-xs">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span>{d.nombre}</span>
                      {d.es_documento_declara && <span className="text-[9px] text-blue-600">🔵 DECLARA+</span>}
                      {d.resolucion_jne && (
                        <Link to={`/normativa?q=${encodeURIComponent(d.resolucion_jne)}`}
                              className="text-[9px] text-arpp-primary hover:underline"
                              title="Ver resolución JNE">
                          📜 Res. {d.resolucion_jne}
                        </Link>
                      )}
                    </div>
                    {d.notas_revision && <div className="text-[10px] text-red-700 mt-0.5">⚠ {d.notas_revision}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded ${eb.cls}`}>{eb.label}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {d.archivo_nombre ? (
                      <div className="flex items-center gap-1">
                        <FileText size={12} className="text-gray-500" />
                        <span className="truncate max-w-[200px]">{d.archivo_nombre}</span>
                        <span className="text-gray-400">({formatSize(d.archivo_tamano_bytes)})</span>
                      </div>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {d.archivo_url && (
                        <a href={d.archivo_url} target="_blank" rel="noopener" className="text-arpp-primary hover:bg-gray-100 px-2 py-1 rounded text-[11px] inline-flex items-center gap-1"><Download size={12} /></a>
                      )}
                      {canEdit && (
                        <>
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" ref={(el) => (fileInputRefs.current[d.codigo] = el)} className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMut.mutate({ codigo: d.codigo, file: f }); e.target.value = ''; }} />
                          <button onClick={() => fileInputRefs.current[d.codigo]?.click()} disabled={isUp}
                            className="text-arpp-primary hover:bg-gray-100 px-2 py-1 rounded text-[11px] inline-flex items-center gap-1">
                            <Upload size={12} /> {isUp ? '...' : (d.archivo_url ? 'Cambiar' : 'Subir')}
                          </button>
                        </>
                      )}
                      {canApprove && d.estado === 'recibido' && (
                        <>
                          <button onClick={() => aprobarMut.mutate(d.id)} className="text-green-700 hover:bg-green-50 px-2 py-1 rounded text-[11px] inline-flex items-center gap-1"><Check size={12} /> Aprobar</button>
                          <button onClick={() => { const m = prompt('Motivo:'); if (m) rechazarMut.mutate({ docId: d.id, motivo: m }); }} className="text-red-700 hover:bg-red-50 px-2 py-1 rounded text-[11px] inline-flex items-center gap-1"><X size={12} /> Rechazar</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div className="border rounded p-2 bg-gray-50 text-center">
          <div className="text-xl font-bold">{totales.total}</div>
          <div className="text-[10px] text-gray-600">DOCS LISTA</div>
        </div>
        <div className="border rounded p-2 bg-green-50 text-center">
          <div className="text-xl font-bold text-green-700">{totales.aprobados}</div>
          <div className="text-[10px] text-gray-600">APROBADOS</div>
        </div>
        <div className="border rounded p-2 bg-yellow-50 text-center">
          <div className="text-xl font-bold text-yellow-800">{totales.recibidos}</div>
          <div className="text-[10px] text-gray-600">POR REVISAR</div>
        </div>
        <div className="border rounded p-2 bg-gray-100 text-center">
          <div className="text-xl font-bold text-gray-700">{totales.pendientes}</div>
          <div className="text-[10px] text-gray-600">PENDIENTES</div>
        </div>
        <div className="border rounded p-2 bg-red-50 text-center">
          <div className="text-xl font-bold text-red-700">{totales.rechazados}</div>
          <div className="text-[10px] text-gray-600">RECHAZADOS</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-2 flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
        <strong>⚖️ OFICIAL JNE</strong> — Requeridos por procedimiento de inscripción (Res. 0002-2026 para REGIONAL, 0848-2025 para MUNICIPAL).
        Estos son los que el JEE verifica al recibir la solicitud.
      </div>
      <GrupoTabla titulo="Oficial JNE (lista)" items={oficiales} intent="oficial" />

      {internos.length > 0 && <>
        <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs text-gray-700">
          <strong>📁 INTERNO ARPP</strong> — Solicitados por la organización política (no requeridos directamente por el JEE).
        </div>
        <GrupoTabla titulo="Interno ARPP (lista)" items={internos} intent="interno" />
      </>}
    </section>
  );
}
